// activity/components/MyUnstakes/UnstakeTabContent.js
import UnstakeTable from "./UnstakeTable";
import PaginationSection from "../../../../../shared/PaginationSection";
import { useEffect, useState } from "react";
import { apiRequest } from "../../../../../api/apiService";
import { UNSTAKE_REQUESTS_QUERY, UNSTAKES } from "../../../../../api/queries";
import { unifarmFactory } from "../../../../../utils/contract";
import { formatUnits } from "viem"; // ✅ use formatUnits instead of formatEther
import { useStake } from "../../../../../hooks/useStake";
import Loader from "../../../../../shared/Loader";
import MobileUnstakeCom from "./MobileUnstakeCom";
import { ActionSuccessModal } from "../../../models";

export default function UnstakeTabContent({
  staker,
  contractAddress,
  claimContract,
  token,
  tokenDecimals = 18, // ✅ fallback to 18 decimals if not passed
}) {
  const [unstakedData, setUnstakedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const { claimUnstaked, estimateClaimUnstakedGas, isClaimingUnstaked } =
    useStake();

  // ✅ Modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalData, setModalData] = useState({
    amount: "",
    tokenSymbol: "",
    txHash: "",
  });

  useEffect(() => {
    if (staker && contractAddress) {
      getMyUnstakes();
      getUnstakesCount();
    }
  }, [staker, contractAddress, currentPage, perPage]);

  const getRequestSets = async () => {
    try {
      const skip = (currentPage - 1) * perPage;
      const first = perPage;
      setLoading(true);

      const response = await apiRequest({
        url: unifarmFactory.rootTokens,
        method: "POST",
        data: {
          query: `
          query MyQuery($where: RequestSet_filter, $skip: Int, $first: Int) {
            requestSets(
              where: $where,
              skip: $skip,
              first: $first,
              orderBy: blockTimestamp,
              orderDirection: desc
            ) {
              id
              index
              contract
              claimer
              transactionHash
            }
          }
        `,
          variables: {
            where: {
              contract: claimContract,
              claimer: staker,
            },
            skip,
            first,
          },
        },
        auth: false,
      });

      return response?.data?.requestSets || [];
    } catch (err) {
      console.error("Error fetching requestSets:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getIsClaimeds = async () => {
    try {
      const skip = (currentPage - 1) * perPage;
      const first = perPage;
      setLoading(true);

      const response = await apiRequest({
        url: unifarmFactory.rootTokens,
        method: "POST",
        data: {
          query: `
          query GetIsClaimeds(
            $where: IsClaimed_filter
            $skip: Int = 0
            $first: Int = 10
            $orderBy: IsClaimed_orderBy = blockTimestamp
            $orderDirection: OrderDirection = desc
          ) {
            isClaimeds(
              where: $where
              skip: $skip
              first: $first
              orderBy: $orderBy
              orderDirection: $orderDirection
            ) {
              id
              index
              contract
              claimer
              transactionHash
            }
          }
        `,
          variables: {
            where: {
              contract: claimContract,
              claimer: staker,
            },
            skip,
            first,
            orderBy: "blockTimestamp",
            orderDirection: "desc",
          },
        },
        auth: false,
      });

      return response?.data?.isClaimeds || [];
    } catch (err) {
      console.error("Error fetching isClaimeds:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getMyUnstakes = async () => {
    try {
      const skip = (currentPage - 1) * perPage;
      const first = perPage;
      setLoading(true);

      // Fetch related data
      const [requestSets, isClaimedsResponse] = await Promise.all([
        getRequestSets(),
        getIsClaimeds(),
      ]);

      const claimedIndexes = new Set(
        isClaimedsResponse.map((item) => Number(item.index))
      );

      // Fetch unstake requests
      const response = await apiRequest({
        url: unifarmFactory.rootTokens,
        method: "POST",
        data: {
          query: UNSTAKE_REQUESTS_QUERY,
          variables: {
            where: {
              staker: staker,
              contract: contractAddress,
            },
            skip,
            first,
            orderBy: "blockTimestamp",
            orderDirection: "desc",
          },
        },
        auth: false,
      });

      const stakeDatas = response?.data?.unstakeRequesteds || [];

      // Merge + format
      const mergedData = stakeDatas.map((item, index) => {
        const match = requestSets.find(
          (r) => r.transactionHash === item.transactionHash
        );
        const unstakeIndex = match ? Number(match.index) : null;
        const unstakeDate = new Date(Number(item.blockTimestamp) * 1000);
        const claimDate = new Date(Number(item.claimTime) * 1000);

        const formattedUnstakeDate = unstakeDate.toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        });
        const formattedUnstakeTime = unstakeDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        const now = new Date();
        const timeDiffMs = claimDate - now;
        let availableIn = "";
        let canWithdraw = false;

        if (timeDiffMs > 0) {
          const totalMinutes = Math.floor(timeDiffMs / 1000 / 60);
          const days = Math.floor(totalMinutes / (60 * 24));
          const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
          const minutes = totalMinutes % 60;
          availableIn = `${days.toString().padStart(2, "0")}D:${hours
            .toString()
            .padStart(2, "0")}H:${minutes.toString().padStart(2, "0")}M`;
        } else {
          availableIn = "Available now";
          canWithdraw = true;
        }

        if (unstakeIndex !== null && claimedIndexes.has(unstakeIndex)) {
          canWithdraw = false;
        }

        // ✅ Apply correct decimals using formatUnits
        const formattedAmount = formatUnits(
          item?.amount || "0",
          tokenDecimals
        );

        return {
          index: unstakeIndex,
          date: formattedUnstakeDate,
          unstakedTime: formattedUnstakeTime,
          claimDate: claimDate.toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          }),
          claimTime: claimDate.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
          amount: formattedAmount, // ✅ decimals applied
          contract: item.contract,
          txHash: item.transactionHash,
          availableIn,
          canWithdraw,
          availableClass: canWithdraw ? "greenline" : "grayline",
        };
      });

      setUnstakedData(mergedData);
    } catch (err) {
      console.error("Error fetching unstake data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getUnstakesCount = async () => {
    try {
      setLoading(true);
      const response = await apiRequest({
        url: unifarmFactory.rootTokens,
        method: "POST",
        data: {
          query: UNSTAKES,
          variables: {
            where: {
              unStaker: staker,
              contract: contractAddress,
            },
          },
        },
        auth: false,
      });

      const stakeDatas = Number(response?.data?.unStakes[0]?.totalUnstaked || 0);
      setTotalItems(stakeDatas);
    } catch (err) {
      console.error("Error fetching unstake count:", err);
    } finally {
      setLoading(false);
    }
  };

  /** ---------- HANDLE CLAIM UNSTAKED ---------- **/
  const handleClaimUnstaked = async (index) => {
    try {
      // console.log(`Claiming unstaked index ${index}...`);
      const { hash } = await claimUnstaked(claimContract, index);
      // console.log("Claim unstaked tx hash:", hash);

      // Find claimed unstake details
      const claimedItem = unstakedData.find((item) => item.index === index);

      // ✅ Set modal data and open it
      setModalData({
        amount: claimedItem?.amount || "0",
        tokenSymbol: token?.symbol || "",
        txHash: hash,
      });
      setShowSuccessModal(true);

      // Optionally refresh data
      await getMyUnstakes();
    } catch (error) {
      console.error("Claim unstaked failed:", error);
    }
  };

  const handleModalClose = async () => {
    setShowSuccessModal(false);
    await getMyUnstakes();
  };

  if (isClaimingUnstaked) {
    return <Loader loadingText={"Withdrawing Amount"} />;
  }

  return (
    <div className="right_side_data">
      <div className="simple_table">
        <UnstakeTable
          data={unstakedData}
          claimContract={claimContract}
          onClaimUnstaked={handleClaimUnstaked}
          isClaimingUnstaked={isClaimingUnstaked}
          estimateClaimUnstakedGas={estimateClaimUnstakedGas}
          token={token}
        />
      </div>

      <MobileUnstakeCom
        data={unstakedData}
        claimContract={claimContract}
        onClaimUnstaked={handleClaimUnstaked}
        isClaimingUnstaked={isClaimingUnstaked}
        estimateClaimUnstakedGas={estimateClaimUnstakedGas}
        token={token}
      />

      <PaginationSection
        currentPage={currentPage}
        totalItems={totalItems}
        perPage={perPage}
        setCurrentPage={setCurrentPage}
        setPerPage={setPerPage}
      />

      {/* ✅ Success Modal Integration */}
      <ActionSuccessModal
        show={showSuccessModal}
        handleClose={handleModalClose}
        title="Success"
        heading="Withdrawal Successful!"
        message="You have successfully withdrawn your unstaked tokens."
        amount={modalData.amount}
        tokenSymbol={modalData.tokenSymbol}
        txHash={modalData.txHash}
        viewTx={`https://etherscan.io/tx/${modalData.txHash}`}
      />
    </div>
  );
}
